import styled, { keyframes } from 'styled-components';

// ==================== 动画定义 ====================
export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const scaleIn = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

export const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// ==================== 样式组件 ====================
export const PageWrapper = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`;

export const Container = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  padding: 24px;
  animation: ${fadeIn} 0.5s ease-in;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;

  h1 {
    color: #333;
    font-size: 24px;
    margin-bottom: 6px;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  .subtitle {
    color: #666;
    font-size: 12px;
  }
`;

export const ProgressBarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 16px;
    left: 0;
    right: 0;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;
  }
`;

export const ProgressLine = styled.div`
  position: absolute;
  top: 16px;
  left: 0;
  height: 2px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  z-index: 1;
  transition: width 0.5s ease;
  width: ${props => props.width}%;
`;

export const ProgressStep = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  .step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: white;
    border: 2px solid ${props => props.active ? '#667eea' : props.completed ? '#4caf50' : '#e0e0e0'};
    background: ${props => props.active ? '#667eea' : props.completed ? '#4caf50' : 'white'};
    color: ${props => (props.active || props.completed) ? 'white' : '#999'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 13px;
    margin-bottom: 6px;
    transition: all 0.3s ease;
    transform: ${props => props.active ? 'scale(1.05)' : 'scale(1)'};
  }

  .step-label {
    font-size: 11px;
    color: ${props => props.active ? '#667eea' : '#999'};
    font-weight: ${props => props.active ? '600' : 'normal'};
    text-align: center;
    max-width: 70px;

    @media (max-width: 768px) {
      font-size: 10px;
      max-width: 55px;
    }
  }
`;

export const Content = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const StatusCard = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
  width: 100%;
  animation: ${slideIn} 0.4s ease;

  .status-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
  }

  .status-icon {
    font-size: 40px;
    flex-shrink: 0;
    line-height: 1;
  }

  .status-text {
    flex: 1;
    text-align: left;
  }

  .status-title {
    font-size: 20px;
    color: #333;
    margin-bottom: 8px;
    font-weight: 600;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }

  .status-description {
    color: #666;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
  }

  .success-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 30px;

    button {
      width: clamp(200px, 24vw, 260px);
      margin: 0;
    }
  }

  /* 保持按钮等其他内容居中 */
  > button,
  > div:not(.status-header) {
    text-align: center;
  }

  @media (max-width: 768px) {
    .status-header {
      gap: 12px;
      margin-bottom: 16px;
    }

    .status-icon {
      font-size: 32px;
    }

    .status-description {
      font-size: 13px;
    }

    .success-actions {
      align-items: center;
      flex-direction: column;
      gap: 10px;

      button {
        width: min(78vw, 280px);
      }
    }
  }
`;

export const Button = styled.button`
  padding: 10px 28px;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 4px;

  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 3px 12px rgba(102, 126, 234, 0.4);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 16px rgba(102, 126, 234, 0.6);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: white;
    color: #667eea;
    border: 2px solid #667eea;

    &:hover {
      background: #f5f7ff;
      transform: translateY(-2px);
    }
  `}

  ${props => props.variant === 'success' && `
    background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
    color: white;
    box-shadow: 0 3px 12px rgba(76, 175, 80, 0.4);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 16px rgba(76, 175, 80, 0.6);
    }
  `}

  ${props => props.small && `
    padding: 6px 16px;
    font-size: 13px;
  `}
`;

export const InfoCard = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px 16px;
  margin: 16px 0;
  text-align: left;

  h4 {
    color: #333;
    margin-bottom: 10px;
    font-size: 14px;
  }
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }

  .info-time {
    color: #666;
    font-weight: 500;
    width: 140px;
    flex-shrink: 0;
    margin-right: 12px;
  }

  .info-label {
    color: #666;
    font-weight: 500;
    flex: 1;
    text-align: left;
  }

  .info-value {
    color: #333;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 12px;
    row-gap: 4px;
    align-items: start;

    .info-time {
      grid-column: 1 / -1;
      justify-self: start;
      width: auto;
      margin-right: 0;
      min-height: 16px;
      font-size: 12px;
      text-align: left;
    }

    .info-label {
      min-width: 0;
    }

    .info-value {
      max-width: 45vw;
      text-align: right;
      overflow-wrap: anywhere;
    }
  }
`;

export const CertItems = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
  margin: 16px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const CertItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8f9fa;
  padding: 14px;
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
`;

export const CertItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .cert-item-icon {
    font-size: 24px;
  }

  .cert-item-info {
    flex: 1;

    h3 {
      color: #333;
      margin-bottom: 2px;
      font-size: 14px;
    }

    p {
      color: #666;
      font-size: 12px;
      margin: 0;
    }
  }
`;

export const CertStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.completed ? '#d4edda' : '#fff3cd'};
  color: ${props => props.completed ? '#155724' : '#856404'};
  white-space: nowrap;
`;

export const ComparisonTable = styled.table`
  width: 100%;
  margin: 20px 0;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  th {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 15px;
    text-align: left;
    font-weight: 600;
  }

  td {
    padding: 15px;
    border-bottom: 1px solid #e0e0e0;
    color: #333;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

export const SuccessAnimation = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${scaleIn} 0.5s ease;
  flex-shrink: 0;

  &::before {
    content: '✓';
    font-size: 36px;
    color: white;
  }
`;

export const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 20px auto;
`;

export const DebugPanel = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px;
  border-radius: 10px;
  font-size: 12px;
  z-index: 1000;

  h4 {
    margin-bottom: 10px;
    color: #ffd700;
  }

  button {
    background: #667eea;
    color: white;
    border: none;
    padding: 5px 10px;
    margin: 3px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 11px;

    &:hover {
      background: #764ba2;
    }
  }

  p {
    margin-top: 10px;
    font-size: 11px;
    color: #aaa;
  }
`;

export const UsersSection = styled.div`
  margin-top: 40px;
  padding: 30px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;

  h3 {
    text-align: center;
    margin-bottom: 32px;
    color: #2c3e50;
    font-size: 24px;
    font-weight: 600;
  }
`;

export const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const UserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .user-avatar {
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
  }

  .user-name {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .user-role {
    font-size: 13px;
    color: #666;
    font-weight: 500;
  }
`;

// ==================== 底部抽屉样式 ====================
const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

export const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.visible ? 'block' : 'none'};
  animation: ${fadeIn} 0.3s ease;
`;

export const DrawerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 1000;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  transform: ${props => props.visible ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${props => props.visible ? slideUp : 'none'} 0.3s ease;
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-radius: 20px 20px 0 0;

  h3 {
    font-size: 16px;
    color: #333;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 22px;
    color: #999;
    cursor: pointer;
    padding: 4px 8px;
    transition: all 0.2s ease;

    &:hover {
      color: #333;
      transform: scale(1.1);
    }
  }
`;

export const DrawerContent = styled.div`
  padding: 20px;
`;

export const FloatingButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-size: 22px;
  cursor: pointer;
  box-shadow: 0 3px 16px rgba(102, 126, 234, 0.5);
  transition: all 0.3s ease;
  z-index: 998;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 5px 24px rgba(102, 126, 234, 0.7);
  }

  &:active {
    transform: translateY(-2px) scale(1);
  }
`;

